import isString from 'lodash/isString';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { SubclipParams } from './subclipDto';
import type { StoredArtifactReference } from '@mimir/primitive/artifactReference';
export const itemArtefacts = <const>['highres', 'proxy', 'aaf'];

/**
 * The types of artefact we can download for an item.
 */
export type ItemArtefact = typeof itemArtefacts[number];

export const isArtefact = (val: unknown): val is ItemArtefact => isString(val) && itemArtefacts.includes(val as ItemArtefact);

export type DownloadProgressUnit = 'byte' | 'part';
export interface DownloadProgressDto {
  // Time indications in seconds
  elapsedTime: number;
  estimatedRemainingTime: number | null;

  // Estimated transfer speed in bytes per second
  speed: number | null;

  // Size indications in DownloadProgressUnit
  totalSize: number;
  transferred: number;

  progressUnit?: DownloadProgressUnit;
  transientError?: string;
}

export enum TransferStateDto {
  PREPROCESSING = 'preprocessing',
  PROGRESSING = 'progressing',
  COMPLETE = 'complete',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

export interface TransferRequestPutRequestDto {
  id: string;
  storageId: string;
  originalFileName?: string;
  targetFileName?: string;
  createLinkForTargetFileName?: boolean;
  virtualClipItemId?: string;
  tenantId?: string;
  storageManagerInstanceId?: string;
  createdOn?: string;
  transferState?: TransferStateDto;
  modifiedOn?: string;
  downloadProgress?: DownloadProgressDto;
  title?: string,

  // isProxy is the legacy boolean value that
  // artefact replaces
  isProxy?: boolean;
  artefact?: ItemArtefact;

  highresIsOffline?: boolean;
  errorCount?: number;

  statusMessage?: string;
}

export type TransferRequestDto = TransferRequestPutRequestDto & {
  // We don't need the item ID when updating a transfer request
  // but it is always present in data returned by the server
  itemId: string;
  mediaFrameRate?: number;
};

export type MonitoredTransfer = Omit<TransferRequestDto, 'storageId' | 'itemId'>;


export type DeleteTransferRequestDto = {
  transferState?: TransferStateDto;
};

export interface TransferRequestPostRequestDto {
  itemId: string;
  storageId: string;
  transferState?: TransferStateDto;
  title?: string;

  // isProxy is the legacy boolean value that
  // artefact replaces
  isProxy?: boolean;
  artefact?: ItemArtefact;

  targetFileName?: string;
  createLinkForTargetFileName?: boolean; // whether to create hard link to the GUID-based file for the targetFileName
  virtualClipItemId?: string;
  subclipParams?: SubclipParams;
}

export interface TransferRequestPostRequestDtoV2 {
  version: 'v2';
  itemId: string;
  storageId: string;
  artefact: ItemArtefact;
  artifactReference: StoredArtifactReference;
  targetFileName: string | undefined;
  createLinkForTargetFileName: boolean; // whether to create hard link to the GUID-based file for the targetFileName
}

export const isTransferRequestPostRequestDto = (obj: unknown): obj is TransferRequestPostRequestDto => {
  /**
   * Though the storageId is not optional in DTO definition but it is optional in API endpoint
   */
  return isObject(obj) && 'itemId' in obj;
};

export const isTransferRequestPostRequestDtoV2 = (obj: unknown): obj is TransferRequestPostRequestDtoV2 => {
  /**
   * Though the storageId is not optional in DTO definition but it is optional in API endpoint
   */
  return isObject(obj) && 'version' in obj && obj.version === 'v2';
};

const validStates = <const>['ready'];
export interface MarkTransferRequestDto {
  state: typeof validStates[number];
  message?: string;
}
export const isMarkTransferRequestDto = (obj: unknown): obj is MarkTransferRequestDto => {
  if (!isObject(obj)) {
    return false;
  }
  const possibleDto = obj as Partial<MarkTransferRequestDto>;
  return !!(
    possibleDto.state && validStates.includes(possibleDto.state)
    && (!possibleDto.message || typeof possibleDto.message === 'string')
  );
};
